import { computed, inject, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { finalize } from 'rxjs/operators';
import { addFundChartCalcs } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/calcs/calcs.actions';
import { addFundChartSecurities, removeFundChartSecurity, setFundChartSecurities, toggleFundChartSecurity, updateFundChartSecurityOrdering, } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/securities/securities.actions';
import { addSecuritiesInfo } from 'SecuritiesApp/ts/state/securities-info/securities-info.actions';
import { getChartOptions } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/chart-options/chart-options.selectors';
import { loadingSecurities, partitionSecuritiesAndLists, } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/securities/securities.selectors';
import { FundChartSeriesType } from 'ChartApp/ts/enums/fund-chart-common.enum';
import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { SecuritiesService } from 'SecuritiesApp/ts/services/securities.service';
import equal from 'Angular/utils/equal/deep-equal';
import * as i0 from "@angular/core";
import * as i1 from "../../../../../../../../../ycharts/static/ts/features/components/draggable-security-list/draggable-security-list.component";
/**
 *
 * Fund Chart Securities List Component
 *
 * This component allows users to select securities.
 * It is in the left sidebar on the fund chart page.
 *
 */
export class FundChartSecuritiesListComponent {
    constructor() {
        this.store = inject(Store);
        this.securitiesService = inject(SecuritiesService);
        this.ajaxService = inject(AjaxService);
        this.urlsService = inject(UrlsService);
        this.storeState = toSignal(this.store.select('reducers'), {
            requireSync: true,
        });
        this.chartOptions = computed(() => getChartOptions(this.storeState()), ...(ngDevMode ? [{ debugName: "chartOptions" }] : []));
        this.calcs = computed(() => this.storeState().fundChart.calcs, ...(ngDevMode ? [{ debugName: "calcs" }] : []));
        this.fetchingSecuritiesInfo = false;
        this.hasSecurityLists = computed(() => {
            const { securityLists } = partitionSecuritiesAndLists(this.storeState());
            return securityLists.length > 0;
        }, ...(ngDevMode ? [{ debugName: "hasSecurityLists" }] : []));
        this.isLoadingSecurities = computed(() => !!loadingSecurities(this.storeState()), ...(ngDevMode ? [{ debugName: "isLoadingSecurities" }] : []));
        this.securities = computed(() => this.storeState().fundChart.securities, ...(ngDevMode ? [{ debugName: "securities" }] : []));
        this.securitiesInfo = computed(() => this.storeState().securitiesInfo, ...(ngDevMode ? [{ debugName: "securitiesInfo" }] : []));
        this.store
            .select('reducers')
            .pipe(takeUntilDestroyed())
            .subscribe((state) => {
            const securities = state.fundChart.securities;
            const securitiesInfo = state.securitiesInfo;
            if (securities.length > 0) {
                this.validateNextSecurities(securities, securitiesInfo);
            }
        });
    }
    validateNextSecurities(securities, securitiesInfo) {
        /**
         * Validates the next set of securities and security lists by doing the following:
         *   1. Remove duplicates
         *   2. If any securities are missing info, fetch their info
         *
         * One thing to keep in mind here is that it needs to preserve the order of securities and lists
         * that were passed in.
         */
        const uniqueSecurities = securities.filter((s, idx, arr) => idx === arr.findIndex((_) => _.id === s.id));
        const securitiesMissingInfo = uniqueSecurities.filter((s) => !securitiesInfo[s.id]).map((sec) => sec.id);
        if (securitiesMissingInfo.length > 0 && !this.fetchingSecuritiesInfo) {
            this.fetchingSecuritiesInfo = true;
            this.securitiesService
                .getSecuritiesInfo(securitiesMissingInfo.join(','))
                .pipe(finalize(() => {
                const validSecurities = uniqueSecurities.filter((sec) => this.securitiesInfo()[sec.id]);
                // Set unique, valid securities regardless of whether the request succeeds
                if (!equal(securities, validSecurities)) {
                    this.store.dispatch(setFundChartSecurities({
                        fundChartSecurities: validSecurities,
                    }));
                }
                this.fetchingSecuritiesInfo = false;
            }))
                .subscribe((data) => {
                this.store.dispatch(addSecuritiesInfo({ securitiesInfo: data }));
            });
        }
        else if (securitiesMissingInfo.length === 0) {
            if (!equal(securities, uniqueSecurities)) {
                this.store.dispatch(setFundChartSecurities({ fundChartSecurities: uniqueSecurities }));
            }
        }
    }
    toggleChartSecurities(fundChartSecurity) {
        this.store.dispatch(toggleFundChartSecurity({
            fundChartSecurity,
        }));
    }
    removeChartSecurities(fundChartSecurity) {
        this.store.dispatch(removeFundChartSecurity({
            fundChartSecurity,
        }));
    }
    updateOrder({ oldIndex, newIndex }) {
        this.store.dispatch(updateFundChartSecurityOrdering({ oldIndex, newIndex }));
    }
    addSecurities(securityIds) {
        this.addMultipleSecurities(securityIds);
    }
    addMultipleSecurities(securityIds) {
        const uniqueSecurityIds = [...new Set(securityIds)];
        const securities = this.securities();
        const calcs = this.calcs();
        this.ajaxService
            .getDataObserveBody(this.urlsService.CHARTS.FUND_CHART_CALC, {
            newSecurityIDs: uniqueSecurityIds.join(','),
            currentSecurityIDs: securities.map((security) => security.id).join(','),
            currentCalcs: calcs.map((calc) => calc.id).join(','),
        })
            .subscribe({
            next: (response) => {
                // only dispatch addFundChartCalc if there is a new calc
                if (response.new_calcs.length) {
                    const metrics = response.new_calcs.map((calc) => ({ id: calc, include: true }));
                    this.store.dispatch(addFundChartCalcs({ metrics }));
                }
                const securitiesInfo = response.new_security_info.map((info) => ({
                    id: info.security_id,
                    security_name: info.security_name,
                    features_needed: info.features_needed,
                    display_security_id: info.display_security_id,
                    inception_date: info.inception_date,
                }));
                const fundChartSecurities = response.new_security_info.map((info) => ({
                    id: info.security_id,
                    include: true,
                    type: FundChartSeriesType.security,
                }));
                this.store.dispatch(addSecuritiesInfo({ securitiesInfo }));
                this.store.dispatch(addFundChartSecurities({
                    fundChartSecurities,
                    fundChartChartOptions: this.chartOptions(),
                }));
            },
        });
    }
}
FundChartSecuritiesListComponent.ɵfac = function FundChartSecuritiesListComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FundChartSecuritiesListComponent)(); };
FundChartSecuritiesListComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: FundChartSecuritiesListComponent, selectors: [["ycn-fund-chart-securities-list"]], standalone: false, decls: 1, vars: 4, consts: [[3, "updateOrder", "addSecurities", "removeSecurity", "toggleSecurity", "securities", "hasSecurityLists", "isLoadingSecurities", "securitiesInfo"]], template: function FundChartSecuritiesListComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "ycn-draggable-security-list", 0);
        i0.ɵɵlistener("updateOrder", function FundChartSecuritiesListComponent_Template_ycn_draggable_security_list_updateOrder_0_listener($event) { return ctx.updateOrder($event); })("addSecurities", function FundChartSecuritiesListComponent_Template_ycn_draggable_security_list_addSecurities_0_listener($event) { return ctx.addSecurities($event); })("removeSecurity", function FundChartSecuritiesListComponent_Template_ycn_draggable_security_list_removeSecurity_0_listener($event) { return ctx.removeChartSecurities($event); })("toggleSecurity", function FundChartSecuritiesListComponent_Template_ycn_draggable_security_list_toggleSecurity_0_listener($event) { return ctx.toggleChartSecurities($event); });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("securities", ctx.securities())("hasSecurityLists", ctx.hasSecurityLists())("isLoadingSecurities", ctx.isLoadingSecurities())("securitiesInfo", ctx.securitiesInfo());
    } }, dependencies: [i1.DraggableSecurityListComponent], encapsulation: 2 });
